import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UserStateEntry, UserStateMap } from "../../types/user-state";
import { deleteUserStateItem } from "./userStateApiClient";
import { userStateQueryKey } from "./userStateQueryKeys";
import { useUser, useWaitForUserStateRestored } from "../UserProvider";
import { syncPreferencesToCookies } from "../../utils/shared/client-preferences";
import {
  applyUserStateEntry,
  resolveDeleteFailureEntry,
  type DeleteUserStateMutationArgs,
} from "./userStateOptimisticQuery";

interface DeleteUserStateMutationContext {
  previousMap: UserStateMap | undefined;
  previousEntry: UserStateEntry | undefined;
}

/**
 * Builds the mutation's `mutationFn`. Split out from the hook so it can be unit tested without
 * rendering (same reasoning as `useUpdateUserStateItem.ts`'s `createUpdateUserStateMutationFn`).
 *
 * - Signed in: unchanged real `DELETE /api/user-state/{itemKey}`.
 * - Guest: `onMutate` (below) already removed the entry from the persisted `userStateQueryKey`
 *   cache — there is no server-side guest state to delete, so this is a deliberate no-op rather
 *   than a network call.
 */
export function createDeleteUserStateMutationFn(isAuthenticated: boolean) {
  return async (args: DeleteUserStateMutationArgs): Promise<void> => {
    if (!isAuthenticated) {
      return;
    }
    await deleteUserStateItem(args);
  };
}

/**
 * TanStack Query port of `useDeleteUserStateItemMutation` + `handleUserStateDeleteLifecycle`'s
 * optimistic-delete / 409-conflict handling. See `useUpdateUserStateItem.ts` for the general
 * shape of the port; the delete case optimistically removes the entry in `onMutate` and, on
 * failure, either restores the previous entry or (on a 409) writes in the server's winning entry
 * — which may itself indicate the item still exists, e.g. it was recreated concurrently. Guest
 * deletes never hit this failure path (no network call to fail).
 */
export function useDeleteUserStateItem() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useUser();
  const waitForUserStateRestored = useWaitForUserStateRestored();

  return useMutation<void, unknown, DeleteUserStateMutationArgs, DeleteUserStateMutationContext>({
    mutationFn: createDeleteUserStateMutationFn(isAuthenticated),
    async onMutate({ itemKey }) {
      // See UserContextValue.waitForUserStateRestored's doc comment (query/UserProvider.tsx) for
      // the data-loss race this prevents.
      await waitForUserStateRestored();
      await queryClient.cancelQueries({ queryKey: userStateQueryKey });

      const previousMap = queryClient.getQueryData<UserStateMap>(userStateQueryKey);
      const previousEntry = previousMap?.[itemKey];

      queryClient.setQueryData<UserStateMap>(userStateQueryKey, (current) =>
        applyUserStateEntry(current ?? {}, itemKey, null)
      );

      return { previousMap, previousEntry };
    },
    onError(error, { itemKey }, context) {
      const { entry } = resolveDeleteFailureEntry(error, context?.previousEntry);
      queryClient.setQueryData<UserStateMap>(userStateQueryKey, (current) =>
        applyUserStateEntry(current ?? context?.previousMap ?? {}, itemKey, entry)
      );
    },
    // No onSuccess cache patch needed: onMutate already removed the entry, and a successful
    // DELETE has no response body to reconcile against.
    // Cache is settled (success or rollback) by the time this fires — re-mirror
    // savedJobs/appliedJobs/viewedJobs into cookies so anonymous SSR (isSaved, match score) sees
    // the change on the next request. See utils/shared/client-preferences.ts.
    onSettled() {
      syncPreferencesToCookies();
    },
  });
}
